import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import { ApplicationForUser } from "../../models/borrower-portal/application-for-user-model";
import { LiveDataTotalModel } from '../../models/borrower-portal/live-data-total-model';
import { PortalContent } from '../../models/borrower-portal/portal-content-model-model';
import { FeatureFlags } from '../../models/feature-flags.model';
import { BasePortalService } from "../../services/base-portal.service";
import { Constants } from '../../services/constants';
import { MortgageApplicationService } from '../../services/mortgage-application.service';
import { NavigationService } from '../../services/navigation.service';
import { NotificationService } from '../../services/notification.service';
import { AgentContactInfo } from '../agent-contact-info/agent-contact-info.component';
import { EConsentDialogComponent } from '../task-information/e-consent-dialog/e-consent-dialog.component';
import { OtherBorrowersOnLoanModel } from '../../models/borrower-portal/other-borrowers-on-loan-model';
import { load_map_and_street_view_from_address } from '../../services/utils';
import { TranslocoService } from '@jsverse/transloco';
import { localizeField } from '../../transloco/localize-field';
import { EnumsService } from '../../services/enums.service';

@Component({
    selector: 'loan-info',
    templateUrl: './loan-info.component.html',
    styleUrls: ['./loan-info.component.scss'],
    standalone: false
})
export class LoanInfoComponent implements OnInit, OnDestroy {

  @Input()
  portalContent: PortalContent;

  @Input()
  isDetailView: boolean

  @Input()
  set application(application: ApplicationForUser) {
    this._application = application;
    this.initialize();
  }

  @Input()
  otherBorrowersOnLoan: OtherBorrowersOnLoanModel[] | undefined;

  get application(): ApplicationForUser {
    return this._application;
  }

  /**
   * The primary borrower on the loan, matched by primaryBorrowerId. When the loan has a known
   * primaryBorrowerId we only return that borrower (never a co-borrower mislabeled as primary);
   * we fall back to the first borrower only when no primary id is known.
   */
  get primaryBorrower(): OtherBorrowersOnLoanModel | undefined {
    const list = this.application?.otherBorrowersOnLoan;
    if (!list?.length) return undefined;
    const primaryBorrowerId = this.application?.primaryBorrowerId;
    if (primaryBorrowerId != null) {
      return list.find(b => b.borrowerId === primaryBorrowerId);
    }
    return list[0];
  }

  @Input()
  showViewTasksButton: boolean;

  @Input()
  showBorrowerContactDetails: boolean;

  @Input()
  isAgent: boolean = false;

  totals: Record<number, LiveDataTotalModel> = {};

  agentAddressHidden: boolean = true;

  agentContactInfo: AgentContactInfo;

  haveEConsent: boolean = false;

  @Input()
  set mobileInfoExpandedDefault(value: boolean) {
    this.mobileInfoExpanded = value;
  }

  mobileInfoExpanded: boolean = false;

  protected applicationTitle: string = '';

  protected loanPurposeDisplay: string = '';

  protected loanStatusDisplay: string = '';

  protected subjectPropertyAddress: string = '';

  private _application: ApplicationForUser;

  private _langChangeSub?: Subscription;

  private readonly _portalService = inject(BasePortalService);
  private readonly _modalService = inject(NgbModal);
  private readonly _spinner = inject(NgxSpinnerService);
  private readonly _onlineAppService = inject(MortgageApplicationService);
  private readonly _notifsService = inject(NotificationService);
  private readonly _navigationService = inject(NavigationService);
  private readonly _router = inject(Router);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _enumsService = inject(EnumsService);

  ngOnInit(): void {
    this.loadTotals();
    // Re-translate cached enum/label display strings when the language switches.
    this._langChangeSub = this._translocoService.langChanges$.pipe(skip(1)).subscribe(() => {
      this.refreshLocalizedLabels();
    });
  }

  ngOnDestroy(): void {
    this._langChangeSub?.unsubscribe();
  }

  onAgentNameClicked = () => {
    this._router.navigate([`${this.isAgent ? 'agent' : 'borrower'}-portal/request-a-call`], { queryParams: { loanId: this.application.applicationId } });
  }

  goToCompleteApplication = (app: ApplicationForUser) => {
    const routeToGo = app.onlineApplicationIsCoborrower ? 'co-apply' : 'apply';
    this._router.navigate(
      [routeToGo],
      {
        queryParams: {
          companyGuid: app.companyGuid,
          appId: app.applicationId,
          flowGuid: app.onlineApplicationVersion,
        },
        queryParamsHandling: 'merge'
      });
  }

  goToDetails = (app: ApplicationForUser) => {
    if (!app.forceFinishOnlineApp) {
      const companyGuid = this._navigationService.companyGuid;
      this._spinner.show();
      this._router.navigate([`${this.isAgent ? 'agent' : 'borrower'}-portal/loans`, app.applicationId], { queryParams: { companyGuid: companyGuid } });
    }
  }

  loadTotals = () => {
    this._portalService.getLiveData()
      .subscribe({
        next: (totals) => {
          this.totals = totals.applicationTotals as any;
        },
        error: (err) => {
          console.error(err);
        }
      })
  }

  badgeLabel = (count = 0): string => {
    if (!count) {
      return '';
    }
    return count > 9 ? '9+' : !count ? '' : String(count);
  }

  onHaveEConsentChanged = () => {
    if (this.haveEConsent) {
      this.generateEConsentDoc();
    }
    else {
      Swal.fire({
        title: this._translocoService.translate('common.warning'),
        html: this._translocoService.translate('loanInfo.revokeEConsentConfirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this._translocoService.translate('common.yes'),
        cancelButtonText: this._translocoService.translate('common.no'),
        reverseButtons: true,
      }).then((modalResult: any) => {
        if (modalResult.value) {
          this.revokeEConsent();
        }
        else {
          this.haveEConsent = true;
        }
      });
    }
  }

  private generateEConsentDoc = () => {
    const appId = this.application.applicationId;

    this._onlineAppService.getConfig(appId)
      .subscribe({
        next: (config: FeatureFlags) => {
          const eConsentText = localizeField(config, 'eConsentText', this._translocoService.getActiveLang());
          const borrowerId = this.application.myDetails.borrowerId;

          const modalRef = this._modalService.open(EConsentDialogComponent, Constants.modalOptions.large);
          modalRef.componentInstance.loanId = appId;
          modalRef.componentInstance.eConsentText = eConsentText;

          modalRef.result.then((res: boolean) => {
            if (!res) {
              this.haveEConsent = false;
              return alert('E-Consent authorization is required for e-signing your documents.');
            }

            this._spinner.show();

            this._portalService.acceptESignConsent(appId, borrowerId)
              .subscribe({
                next: () => {

                  this._onlineAppService.generateEConsentDocument(appId, { eConsentText: eConsentText, borrowerId: borrowerId })
                    .subscribe({
                      next: () => {
                        this._spinner.hide();
                        this._notifsService.showInfo("We've successfully received your e-consent.", "E-consent");
                      },
                      error: (error) => {
                        console.error(error.error.message ? error.error.message : 'Document generation failed');
                        this._spinner.hide();
                      }
                    })
                },
                error: (error) => {
                  console.error(error.error.message ? error.error.message : 'Accept E-Sign Consent failed');
                  this._spinner.hide();
                }
              });
          });

        },
        error: (err) => {
          console.error(err);
        }
      })
  }

  private revokeEConsent = () => {
    this._spinner.show();
    this._portalService.revokeEConsent(this.application.applicationId, this.application.myDetails.borrowerId).subscribe({
      next: () => {
        this._spinner.hide();
        this._notifsService.showInfo("Your e-consent has been successfully revoked.", "E-consent");
      },
      error: (error) => {
        console.error(error.error.message ? error.error.message : 'Revoke E-Consent failed');
        this._spinner.hide();
      }
    })
  }

  private loadContactInfo = (appId: number) => {
    if (appId) {
      this._portalService.getContactInfoForApplication(appId).subscribe({
        next: (contactInfo) => {
          if (contactInfo) {
            this.agentContactInfo = contactInfo as unknown as AgentContactInfo;
            this.agentContactInfo.calendarLink = this.agentContactInfo.calendarLink || this.application.contactCalendarLink;
          }
        },
        error: (err) => {
          console.error(err);
        }
      });
    }
  }

  googleStreetViewUrl: string = "";

  // Builds the display strings that depend on the active language, so they can be
  // rebuilt on a language switch (see the langChanges$ subscription in ngOnInit).
  private refreshLocalizedLabels(): void {
    const loanNumber = this.application?.refNumber || this._application?.applicationId;
    this.applicationTitle = `${this._translocoService.translate('portal.loanInfo.filePrefix')}${loanNumber}`;

    if (!this.isAgent) {
      if (this.application?.myDetails?.borrowerName) {
        this.applicationTitle += ` - ${this.application.myDetails.borrowerName}`;
      }
    } else {
      const primaryBorrowerName = this.primaryBorrower?.borrowerName;
      if (primaryBorrowerName) {
        this.applicationTitle += ` - ${primaryBorrowerName}`;
      }
    }

    this.loanPurposeDisplay = this._enumsService.loanPurposeName(this.application?.loanPurpose);
    // Loan status is admin-configured per-company text localized server-side via _i18n; resolve the
    // active language with fallback to the English base (same convention as the consent texts).
    this.loanStatusDisplay = localizeField(this.application, 'loanStatus', this._translocoService.getActiveLang()) ?? '';
  }

  // Mobile status pill: tapping it opens the blurb popover (see template). Only swallow the tap when
  // there is a blurb to show, so a status without one still toggles the collapsible header as before.
  onMobileStatusClicked(event: Event): void {
    if (this.application?.blurb) {
      event.stopPropagation();
    }
  }

  private initialize = () => {
    this.haveEConsent = !!this.application?.myDetails?.acceptedEConsentDate;
    this.refreshLocalizedLabels();

    this.subjectPropertyAddress = `${this.application?.address || 'No Street Address'} ${this.application?.city ? this.application?.city + "," : ""} ${this.application?.state || ''} ${this.application?.zip || ''}`;

    this.loadContactInfo(this.application.applicationId);

    // Don't geocode / load Google Maps + Street View for a TBD property address (e.g. new construction).
    // Reset first so a reused component instance doesn't keep a stale "View on Google Maps" link over the fallback.
    this.googleStreetViewUrl = "";
    const isTbdAddress = this.application?.address?.trim().toLocaleLowerCase() === 'tbd';
    const streetViewAddress = (this.application?.address && !isTbdAddress) ? this.subjectPropertyAddress : "";
    load_map_and_street_view_from_address(
      streetViewAddress,
      "pano_" + this.application.applicationId,
      (panoState) => {
        if (panoState) {
          this.googleStreetViewUrl = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${panoState.lat},${panoState.lng}&pano=${panoState.panoId}&heading=${panoState.heading}&pitch=${panoState.pitch}&fov=80`;
        }
      },
      this.portalContent?.isGoogleStreetViewImageDisabled
    );
  }
}
